<template>
  <v-row dense align="center">
    <v-menu offset-y :close-on-content-click="false">
      <template v-slot:activator="{ on, attrs }">
        <v-badge bordered dot color="error" overlap :value="isFiltersDirty">
          <v-btn small text outlined v-on="on" :attrs="attrs" color="primary"
            ><v-icon>mdi-filter</v-icon>Filters</v-btn
          >
        </v-badge>
      </template>
      <v-card max-width="1000px" style="overflow-y: auto">
        <v-card-text>
          <v-row dense>
            <v-col
              v-for="(item, index) in filtersList"
              :key="index"
              cols="12"
              sm="4"
            >
              <v-autocomplete
                v-if="!item.isDateField && !item.isToggle"
                :items="item.options"
                :value="
                  Array.isArray(item.value)
                    ? item.value.map((x) => x)
                    : item.value.id
                "
                item-value="id"
                @change="applyFilter(item, $event)"
                return-object
                clearable
                :multiple="item.allowMultiple"
                :label="item.label"
                hide-details
                dense
                outlined
                class="mx-1"
              ></v-autocomplete>
              <month-picker-field
                v-if="item.isDateField"
                :field="{ label: item.label }"
                :value="item.value.map((x) => x.id)"
                @fieldChange="applyDateFilter(item, $event)"
              ></month-picker-field>
              <v-switch
                v-if="item.isToggle"
                v-model="item.value"
                @change="applyFilter(item, $event)"
                x-small
                hide-details
                class="mx-1 mt-1"
              >
                <template v-slot:label>
                  <span class="text-body-2">{{ item.label }}</span></template
                ></v-switch
              >
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-menu>
    <v-chip-group column>
      <template v-for="(filter, i) in appliedFilters">
        <v-chip
          :key="i"
          label
          close
          close-icon="mdi-close"
          @click:close="removeFilter(filter)"
          color="primary"
          dark
          small
          class="mx-1"
          >{{ filter.label }}: {{ filter.text }}</v-chip
        >
      </template>
    </v-chip-group>
  </v-row>
</template>

<script>
import { formatDate } from "../../utils/format-utils";
import MonthPickerField from "../FormFields/month-picker-field.vue";
export default {
  components: { MonthPickerField },
  computed: {
    isFiltersDirty() {
      return this.$store.getters["plansModule/isFiltersDirty"];
    },
    semesters() {
      return (
        this.$store.state.optionsModule.semesters.filter(
          (x) => new Date(x.semesterStartDate) >= new Date(2023, 9, 1)
        ) || []
      );
    },
    teamOptions() {
      var teams = [{ text: "No Team", id: "none" }];
      teams.push(
        ...this.clientRoster
          .filter((item) => item.teamId != null)
          .map((item) => {
            return {
              text: item.teamName,
              id: item.teamId,
            };
          })
          .sort((a, b) => a.text.localeCompare(b.text))
      );
      return teams;
    },
    schoolOptions() {
      var schools = [{ text: "No School", id: "none" }];
      schools.push(
        ...this.clientRoster
          .filter((item) => item.schoolId != null)
          .map((item) => {
            return {
              text: item.schoolName,
              id: item.schoolId,
            };
          })
          .sort((a, b) => a.text.localeCompare(b.text))
      );
      return schools;
    },
    providerOptions() {
      var providers = [{ text: "No Provider", id: "none" }];
      providers.push(
        ...this.clientRoster
          .filter((item) => item.providerId != null)
          .map((item) => {
            return {
              text: `${item.providerFullName} - ${item.legacyProviderId}`,
              id: item.providerId,
            };
          })
          .sort((a, b) => a.text.localeCompare(b.text))
      );
      return providers;
    },
    // filters
    filtersList() {
      return [
        {
          label: "Client",
          name: "clientFilter",
          value: this.filters.clientFilter || "",
          options: this.clientRoster
            .map((item) => {
              return {
                text: `${item.clientName} (${item.formattedDob})`,
                id: item.clientId,
              };
            })
            .sort((a, b) => a.text.localeCompare(b.text)),
        },
        {
          label: "Year Of Birth",
          name: "yearOfBirthFilter",
          value: this.filters.yearOfBirthFilter || "",
          options: this.clientRoster
            .map((item) => {
              return {
                text: `${item.yearOfBirth} DOB`,
                id: item.yearOfBirth,
              };
            })
            .sort((a, b) => a - b),
        },
        {
          label: "ZIP Code",
          name: "zipCodeFilter",
          allowMultiple: true,
          value: this.filters.zipCodeFilter || "",
          options: this.clientRoster
            .filter((item) => item.zipCode != null && item.zipCode != "")
            .map((item) => {
              return {
                text: item.zipCode,
                id: item.zipCode,
              };
            })
            .sort((a, b) => a.text.localeCompare(b.text)),
        },
        {
          label: "District",
          name: "districtFilter",
          allowMultiple: true,
          value: this.filters.districtFilter || [],
          options: this.clientRoster
            .filter((item) => item.districtId != null)
            .map((item) => {
              return {
                text: item.districtId,
                id: item.districtId,
              };
            })
            .sort((a, b) => a.id - b.id),
        },
        {
          label: "District Admin",
          name: "districtAdminFilter",
          value: this.filters.districtAdminFilter || "",
          options: this.clientRoster
            .filter((item) => item.districtAdminId != null)
            .map((item) => {
              return {
                text: item.districtAdminFullName,
                id: item.districtAdminId,
              };
            })
            .sort((a, b) => a.text.localeCompare(b.text)),
        },
        {
          label: "School",
          name: "schoolFilter",
          value: this.filters.schoolFilter || "",
          options: this.schoolOptions,
        },
        {
          label: "Team",
          name: "teamFilter",
          value: this.filters.teamFilter || "",
          options: this.teamOptions,
        },
        {
          label: "Group Size",
          name: "groupSizeFilter",
          value: this.filters.groupSizeFilter || "",
          options: [
            { text: "1", id: 1 },
            { text: "2", id: 2 },
            { text: "3", id: 3 },
          ],
        },
        {
          label: "10/12 Month",
          name: "tenTwelveMonthFilter",
          value: this.filters.tenTwelveMonthFilter || "",
          options: [
            { text: "10-month", id: "10" },
            { text: "12-month", id: "12" },
          ],
        },
        {
          label: "Language",
          name: "languageFilter",
          value: this.filters.languageFilter || "",
          options: this.clientRoster
            .filter((item) => item.languageId != null)
            .map((item) => {
              return {
                text: item.language,
                id: item.languageId,
              };
            })
            .sort((a, b) => a.text.localeCompare(b.text)),
        },
        {
          label: "Semester",
          name: "semesterFilter",
          value: this.filters.semesterFilter || "",
          options: this.semesters
            .map((item) => {
              return {
                text: item.semesterCode,
                id: item.semesterCode,
              };
            })
            .sort((a, b) => a - b),
        },
        {
          label: "Provider",
          name: "providerFilter",
          value: this.filters.providerFilter || "",
          options: this.providerOptions,
        },
        {
          isDateField: true,
          label: "Plan Approval Start",
          name: "planStartDateFilter",
          value: this.filters.planStartDateFilter.map((item) => item) || [],
        },
        {
          isDateField: true,
          label: "Plan Approval End",
          name: "planEndDateFilter",
          value: this.filters.planEndDateFilter.map((item) => item) || [],
        },
        {
          isDateField: true,
          label: "FAD",
          name: "actualStartDateFilter",
          value: this.filters.actualStartDateFilter.map((item) => item) || [],
        },
        {
          isToggle: true,
          label: "Active Plans",
          name: "activePlansFilter",
          value: this.filters.activePlansFilter,
        },
        {
          isToggle: true,
          label: "Expiring Plans",
          name: "expiringPlansFilter",
          value: this.filters.expiringPlansFilter,
        },
        {
          isToggle: true,
          label: "Awaiting FAD Submission",
          name: "awaitingFADSubmissionFilter",
          value: this.filters.awaitingFADSubmissionFilter,
        },
        {
          isToggle: true,
          label: "Current Assignments",
          name: "currentAssignmentsFilter",
          value: this.filters.currentAssignmentsFilter,
        },
        {
          isToggle: true,
          label: "Pending Assignments",
          name: "pendingAssignmentsFilter",
          value: this.filters.pendingAssignmentsFilter,
        },
        {
          isToggle: true,
          label: "Pending Approval",
          name: "pendingApprovalFilter",
          value: this.filters.pendingApprovalFilter,
        },
        {
          isToggle: true,
          label: "Partial Mandate",
          name: "partialMandateFilter",
          value: this.filters.partialMandateFilter,
        },
      ];
    },
    clientRoster() {
      return this.$store.state.plansModule.clientRoster || [];
    },
    filters() {
      return this.$store.state.plansModule.filters;
    },
    appliedFilters() {
      return this.filtersList
        .filter(
          (item) =>
            item.value === true ||
            (Array.isArray(item.value) && item.value.length > 0) ||
            Object.keys(item.value).length > 0
        )
        .map((item) => {
          return {
            ...item,
            text: item.isToggle
              ? null
              : item.value.text ||
                item.value.map((item) => item.text).join(","),
          };
        });
    },
  },
  methods: {
    formatDate,
    applyDateFilter(filter, value) {
      this.applyFilter(
        filter,
        value.map((item) => {
          return {
            id: item,
            text: `${item.toLocaleString("default", {
              month: "short",
            })} ${item.getFullYear()}`,
          };
        })
      );
    },
    applyFilter(filter, value) {
      if (value == null) {
        this.removeFilter(filter);
      } else {
        // save the value in the store
        this.$store.dispatch("plansModule/setFilters", {
          filterName: filter.name,
          filterValue: value,
        });
      }
    },
    removeFilter(filter) {
      if (filter.isDateField) {
        this.applyFilter(filter, []);
      } else if (filter.isToggle) {
        this.applyFilter(filter, false);
      } else {
        this.applyFilter(filter, {});
      }
    },
  },
};
</script>
